// src/pages/AiAutomation.js
import React from 'react';
import FeaturePageLayout from '../components/FeaturePageLayout';

// Import Icons and Images
import { 
    IoSparklesOutline, 
    IoFlashOutline, 
    IoDuplicateOutline, 
    IoBarChartOutline, 
    IoTimerOutline 
} from 'react-icons/io5';
import image1 from '../assets/images/Generate.png';
import image2 from '../assets/images/CustomTime.png';
import image3 from '../assets/images/Messages.png';
import image4 from '../assets/images/Analytics.png';
import heroBg from '../assets/images/ai-automation-background.png'; 

// --- Structured Data Schemas ---
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [{
    "@type": "ListItem",
    "position": 1,
    "name": "Home",
    "item": "https://oolook.in/"
  },{
    "@type": "ListItem",
    "position": 2,
    "name": "AI & Automation",
    "item": "https://oolook.in/ai-automation"
  }]
};

const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Marketing Automation Software",
    "provider": {
        "@type": "Organization",
        "name": "Oolook"
    },
    "name": "AI-Powered Social Media Automation Platform",
    "description": "Oolook uses AI to automate your entire social media workflow, from content creation and scheduling to analytics and community management. Save time and scale your strategy.",
    "url": "https://oolook.in/ai-automation"
};

// --- Page-Specific Data ---
const aiAutomationPageData = {
  seo: {
    title: "AI & Automation for Social Media | Oolook",
    description: "Put your social media on autopilot. Oolook's AI automates content creation, scheduling, smart replies, and performance reporting so you can focus on strategy.",
    image: 'https://oolook.in/og/ai-automation.png', // Ensure this OG image exists
    schemas: [breadcrumbSchema, serviceSchema]
  },
  hero: {
    icon: IoSparklesOutline,
    title: "Your Intelligent Autopilot for Social Media",
    subtitle: "Stop the manual work. Let Oolook's AI handle the repetitive tasks, from drafting content to analyzing performance, so you can scale your strategy with effortless precision.",
    backgroundImage: heroBg
  },
  featureSection: {
    title: "An AI Co-pilot for Every Task",
    subtitle: "We've integrated intelligent automation into every corner of the platform to make your workflow faster and smarter.",
    features: [
      { title: "Generate Content Instantly", description: "Create a month's worth of on-brand posts from a single prompt. Our AI learns your voice and style.", image: image1 },
      { title: "Automate Your Calendar", description: "Oolook automatically finds the optimal posting times for your audience and publishes your content for you.", image: image2 },
      { title: "Handle Replies Faster", description: "Use AI-suggested replies to manage your community and respond to common questions in a fraction of the time.", image: image3 },
      { title: "Get Automated Insights", description: "Receive scheduled reports and proactive alerts. Our AI surfaces the key metrics and trends you need to know.", image: image4 }
    ]
  },
  benefitGrid: {
    title: "The Compounding Value of Automation",
    benefits: [
      { icon: IoTimerOutline, title: "Reclaim Your Day", description: "Automate 90% of your repetitive social media tasks and free up hours for high-impact strategic work." },
      { icon: IoDuplicateOutline, title: "Scale Your Content", description: "Produce a higher volume of quality, on-brand content without burning out your creative team." },
      { icon: IoFlashOutline, title: "Increase Agility", description: "React to trends and launch campaigns faster than ever before with streamlined, automated workflows." },
      { icon: IoBarChartOutline, title: "Smarter Decisions", description: "Let AI handle the data crunching, so you can focus on making informed, data-driven strategic choices." }
    ]
  },
  stepByStep: {
    title: "Activate Your Autopilot in Minutes",
    subtitle: "Setting up powerful automations in Oolook is designed to be simple and intuitive.",
    steps: [
      { title: "1. Define Your Goal", description: "Tell Oolook what you want to achieve, whether it's generating a new campaign or creating a weekly performance report." },
      { title: "2. Provide a Simple Prompt", description: "Give our AI a few keywords or a simple sentence. For example, 'Create a campaign for our new product launch'." },
      { title: "3. Review and Approve", description: "Oolook will instantly generate the content, schedule, or report. You always have the final say with a single click." },
      { title: "4. Set it and Forget it", description: "Once approved, the automation runs in the background, saving you time every single day." }
    ]
  },
  targetAudience: {
    title: "Intelligent Automation for Every Workflow"
  }
};

const AiAutomation = () => {
  return (
    <FeaturePageLayout pageData={aiAutomationPageData} />
  );
};

export default AiAutomation;